import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { listCategories, analyzeAttributes, buildPack } from '@/api/exportApi'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const PRODUCT_INDEX = 0 // MVP: 仅产品 A

export const useExportStore = defineStore('export', () => {
  const workbench = useWorkbenchStore()

  const categories = ref([])
  const categoriesLoading = ref(false)

  const form = ref({
    sku: '',
    storeAccount: 'cyzp',
    categoryKey: '',
    price: '39.9',
    quantity: '1000',
    sizeList: 'Small(小),Medium(中等),Large(大)',
    brand: '',
    parentSku: ''
  })

  const analyzedFields = ref({}) // key -> value
  const analyzing = ref(false)
  const analyzeError = ref('')

  const building = ref(false)
  const buildError = ref('')

  const sellType = computed(() => {
    const colors = workbench.imageGen?.fissionColors?.[PRODUCT_INDEX] || []
    return colors.length > 0 ? '多变种' : '单品'
  })

  const isMultiVariant = computed(() => sellType.value === '多变种')

  const colorCount = computed(() => {
    if (!isMultiVariant.value) return 0
    const colors = workbench.imageGen?.fissionColors?.[PRODUCT_INDEX] || []
    const fissionCount = colors.filter(c => (c.results || []).some(r => r.url)).length
    const promoSlots = workbench.imageGen?.genResults?.[PRODUCT_INDEX] || []
    const hasPromos = promoSlots.some(s => s.url)
    return (hasPromos ? 1 : 0) + fissionCount
  })

  const sizeValues = computed(() => {
    return form.value.sizeList
      .split(/[,，]/)
      .map(s => s.trim())
      .filter(Boolean)
  })

  const loadCategories = async () => {
    if (categories.value.length > 0) return
    categoriesLoading.value = true
    try {
      categories.value = await listCategories()
      if (!form.value.categoryKey && categories.value.length > 0) {
        form.value.categoryKey = categories.value[0].key
      }
    } catch (e) {
      console.error('加载类别失败', e)
    } finally {
      categoriesLoading.value = false
    }
  }

  const currentCategory = computed(() =>
    categories.value.find(c => c.key === form.value.categoryKey) || null
  )

  const currentTemplateFields = computed(() =>
    currentCategory.value?.fields || []
  )

  const aiDerivableFields = computed(() =>
    currentTemplateFields.value.filter(f => (f.source || 'excel').toLowerCase() === 'excel')
  )

  // 获取当前产品的所有可用图片 URL
  const productImages = computed(() => {
    const pi = PRODUCT_INDEX
    const out = []

    // 主图（产品上传的原图）
    const mainImg = workbench.products?.[pi]?.mainImage
    if (mainImg) out.push({ label: '产品原图', url: mainImg })

    // 宣传图
    const promoSlots = workbench.imageGen?.genResults?.[pi] || []
    promoSlots.forEach((slot, si) => {
      if (slot.url) out.push({ label: `宣传图${si + 1}`, url: slot.url })
    })

    // 裂变图（按颜色分组）
    const colors = workbench.imageGen?.fissionColors?.[pi] || []
    colors.forEach((color, ci) => {
      const fissionResults = color.results || []
      fissionResults.forEach((r, ri) => {
        if (r.url) out.push({
          label: `${color.label || '颜色' + (ci + 1)}_${ri + 1}`,
          url: r.url, ci, ri
        })
      })
    })

    return out
  })

  // 主图（白底，来自 mainImageGen.results）
  const mainWhiteImages = computed(() => {
    const pi = PRODUCT_INDEX
    const results = workbench.mainImageGen?.results || {}
    const out = []

    // 来自裂变图的主图 key: `${pi}-${ci}`
    const colors = workbench.imageGen?.fissionColors?.[pi] || []
    colors.forEach((color, ci) => {
      const key = `${pi}-${ci}`
      const r = results[key]
      if (r?.url) out.push({ label: `主图_${color.label || '颜色' + (ci + 1)}`, url: r.url, ci })
    })

    // 来自宣传图的主图 key: `promo-${pi}-${si}`
    const promoSlots = workbench.imageGen?.genResults?.[pi] || []
    promoSlots.forEach((_, si) => {
      const key = `promo-${pi}-${si}`
      const r = results[key]
      if (r?.url) out.push({ label: `主图_宣传图${si + 1}`, url: r.url, si })
    })

    return out
  })

  // 自动构建图片映射（pack 用）
  // 多变种：每张宣传图 1 个 variant + 每个裂变色 1 个 variant
  const imageMapping = computed(() => {
    const pi = PRODUCT_INDEX
    const map = { single: [], variants: [] }

    if (isMultiVariant.value) {
      const colors = workbench.imageGen?.fissionColors?.[pi] || []
      const mainResults = workbench.mainImageGen?.results || {}
      const promoSlots = workbench.imageGen?.genResults?.[pi] || []

      let vi = 0

      // 所有宣传图合并 = 1 个 variant（颜色 1）
      const promoData = []
      promoSlots.forEach((slot, si) => {
        if (!slot.url) return
        const whiteKey = `promo-${pi}-${si}`
        const white = mainResults[whiteKey]?.url
        promoData.push({ url: slot.url, white })
      })
      if (promoData.length > 0) {
        const first = promoData[0]
        const subUrls = promoData.map(p => p.url)
        map.variants.push({
          vi: vi++,
          type: 'promo',
          colorLabel: '颜色 1',
          colorValue: 'color_1',
          main: first.white || first.url,
          swatch: subUrls[0],
          sub: subUrls
        })
      }

      // 每个裂变色 = 1 个 variant（颜色 2, 3, ...）
      colors.forEach((color, ci) => {
        const fissionResults = (color.results || []).filter(r => r.url)
        if (fissionResults.length === 0) return

        const mainKey = `${pi}-${ci}`
        const whiteMain = mainResults[mainKey]?.url
        const subUrls = fissionResults.map(r => r.url)

        map.variants.push({
          vi: vi++,
          type: 'fission',
          ci,
          colorLabel: `颜色 ${ci + 2}`,
          colorValue: `color_${ci + 2}`,
          main: whiteMain || subUrls[0],
          swatch: subUrls[0],
          sub: subUrls
        })
      })
    } else {
      // 单品：用宣传图 + 主图（白底）
      const promoSlots = workbench.imageGen?.genResults?.[pi] || []
      const mainResults = workbench.mainImageGen?.results || {}
      promoSlots.forEach((slot, si) => {
        if (slot.url) {
          const whiteKey = `promo-${pi}-${si}`
          const white = mainResults[whiteKey]?.url
          map.single.push({ url: white || slot.url, label: `Promo${si + 1}` })
        }
      })
    }

    return map
  })

  // 文案数据（从 workbench 读，使用英文版本）
  const copyData = computed(() => {
    const r = workbench.copywriting?.results?.[PRODUCT_INDEX]
    if (!r) return null
    return {
      title: r.title || r.titleZh || '',
      introduction: r.introduction || r.introductionZh || '',
      features: r.features?.length ? r.features : (r.featuresZh || []),
      keywords: r.keywords || ''
    }
  })

  // 类别变化时，用模板默认值预填 analyzedFields（AI 之后可覆盖）
  const fillDefaults = () => {
    const fields = currentTemplateFields.value
    const next = {}
    fields.forEach(f => {
      const dv = f.defaultValue
      if (!dv || dv === 'false') return
      if ((f.source || 'excel').toLowerCase() !== 'excel') return
      if (f.type === 'multi_checkbox') {
        next[f.key] = [dv]
      } else {
        next[f.key] = dv
      }
    })
    analyzedFields.value = next
  }

  watch(() => form.value.categoryKey, (key, oldKey) => {
    if (key && key !== oldKey) fillDefaults()
  })

  // 触发 AI 分析
  const runAnalyze = async () => {
    if (!form.value.categoryKey) {
      analyzeError.value = '请先选择商品类别'
      return
    }
    const imgs = productImages.value.map(i => i.url)
    if (imgs.length === 0) {
      analyzeError.value = '产品 A 暂无图片，请先到图片生成页生成'
      return
    }
    analyzing.value = true
    analyzeError.value = ''
    try {
      const res = await analyzeAttributes(form.value.categoryKey, imgs)
      analyzedFields.value = { ...analyzedFields.value, ...(res.fields || {}) }
    } catch (e) {
      analyzeError.value = e.message || '分析失败'
    } finally {
      analyzing.value = false
    }
  }

  // 构造完整 product.json
  const buildProductJson = () => {
    if (!currentCategory.value) throw new Error('未选择商品类别')

    const fields = {}
    // _base 字段
    fields.product_title = copyData.value?.title || ''
    fields.parent_sku = form.value.parentSku || form.value.sku
    fields.brand = form.value.brand || ''
    fields.product_category = currentCategory.value.productCategory
    fields.sell_type = sellType.value
    fields.description = copyData.value?.introduction || ''

    // 类别字段：用 AI 分析结果，没有则用 default_value
    currentTemplateFields.value.forEach(f => {
      const aiVal = analyzedFields.value[f.key]
      const hasValue = Array.isArray(aiVal)
        ? aiVal.length > 0
        : (aiVal !== undefined && aiVal !== '')
      if (hasValue) {
        fields[f.key] = aiVal
      } else if (f.defaultValue && f.defaultValue !== 'false') {
        fields[f.key] = f.defaultValue
      }
    })

    // 价格相关
    if (form.value.price) fields['List Price'] = form.value.price
    fields['List Price Currency'] = 'USD(美元)'

    const product = {
      schema_version: '1.0',
      product_category: currentCategory.value.productCategory,
      sell_type: sellType.value,
      store_account: form.value.storeAccount,
      fields
    }

    if (isMultiVariant.value) {
      const sizes = sizeValues.value
      const colors = imageMapping.value.variants.map(v => v.colorValue)
      product.multi_variant = {
        dimensions: [
          { name: 'Color', values: colors },
          ...(sizes.length > 0 ? [{ name: 'Size', values: sizes }] : [])
        ],
        price: form.value.price || '',
        quantity: form.value.quantity || '',
        matrix: buildMatrix(imageMapping.value.variants, sizes)
      }
    } else {
      product.images = imageMapping.value.single.map((_, i) => `images/main_${i + 1}.png`)
    }

    return product
  }

  const buildMatrix = (variants, sizes) => {
    const matrix = []
    variants.forEach(v => {
      const subs = v.sub.map((url, ri) => `images/sub_${v.vi + 1}_${ri + 1}.png`)
      if (sizes.length === 0) {
        matrix.push({
          color: v.colorValue,
          main: `images/main_${v.vi + 1}.png`,
          swatch: `images/swatch_${v.vi + 1}.png`,
          sub: subs
        })
      } else {
        // 第一个 size 行带图片，其他只填 color+size
        sizes.forEach((size, sIdx) => {
          if (sIdx === 0) {
            matrix.push({
              color: v.colorValue,
              size,
              main: `images/main_${v.vi + 1}.png`,
              swatch: `images/swatch_${v.vi + 1}.png`,
              sub: subs
            })
          } else {
            matrix.push({ color: v.colorValue, size })
          }
        })
      }
    })
    return matrix
  }

  // 把 product.json + 图片路径列表打包给后端
  const buildImageList = () => {
    const list = []
    if (isMultiVariant.value) {
      imageMapping.value.variants.forEach(v => {
        if (v.main) list.push({ url: v.main, path: `images/main_${v.vi + 1}.png` })
        if (v.swatch) list.push({ url: v.swatch, path: `images/swatch_${v.vi + 1}.png` })
        v.sub.forEach((url, ri) => {
          if (url) list.push({ url, path: `images/sub_${v.vi + 1}_${ri + 1}.png` })
        })
      })
    } else {
      imageMapping.value.single.forEach((img, i) => {
        list.push({ url: img.url, path: `images/main_${i + 1}.png` })
      })
    }
    return list
  }

  const downloadPack = async () => {
    if (!form.value.sku) {
      buildError.value = '请填写 SKU'
      throw new Error('请填写 SKU')
    }
    building.value = true
    buildError.value = ''
    try {
      const productJson = buildProductJson()
      const images = buildImageList()
      const blob = await buildPack(form.value.sku, productJson, images)
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `pack_${form.value.sku}.zip`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      buildError.value = e.message || '打包失败'
      throw e
    } finally {
      building.value = false
    }
  }

  return {
    categories, categoriesLoading,
    form, analyzedFields, analyzing, analyzeError,
    building, buildError,
    sellType, isMultiVariant, colorCount, sizeValues,
    currentCategory, currentTemplateFields, aiDerivableFields,
    productImages, mainWhiteImages, imageMapping, copyData,
    loadCategories, runAnalyze, downloadPack, buildProductJson
  }
})
